/**
 * Profile Settings Page - Trade Loop v2.0
 *
 * User profile and account settings page
 * Migrated from v1.0 settings.tsx with cookie-based auth
 *
 * Features:
 * - User profile display with avatar
 * - Account settings navigation
 * - Logout functionality
 * - Support and help links
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { useUserTrades } from "@/hooks/useTrades";
import { api } from "@/lib/api";
import AppLayout from "@/components/layout/AppLayout";
import { AppVersionFooter } from '@/components/AppVersionFooter';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import ProfilePreviewModal from "@/components/ProfilePreviewModal";
import ConnectedAccountsCard from "@/components/settings/ConnectedAccountsCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  User,
  Settings,
  HelpCircle,
  MessageSquare,
  CreditCard,
  FileText,
  Trash2,
  LogOut,
  Mail,
  Phone,
  Send,
  Bug,
  Lightbulb,
  Target,
  Check,
  AlertCircle,
  Fingerprint,
  Building2,
  Ban,
  Smartphone,
} from "lucide-react";
import { useBiometric } from "@/hooks/useBiometric";
import { useSubscription } from "@/hooks/useSubscription";
import { TeamCollaborationGate, ProfessionalProfileGate } from "@/components/subscription/FeatureGate";
import { PhoneVerificationModal } from "@/components/ui/PhoneVerificationModal";
import { formatPhoneNumber } from "@/utils/phoneFormatter";
import { platform } from "@/utils/platform";
import { ArrowUpCircle } from "lucide-react";
import AppStoreBadges, { APP_STORE_BADGES_VISIBLE } from "@/components/AppStoreBadges";

export default function ProfileSettingsPage() {
  const { user, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  // Check if user is an organization member (not admin/owner)
  const isOrgMember = user?.organizationMembership?.role === 'member';

  // Fetch user trades from junction table
  const { data: userTrades, isLoading: tradesLoading } = useUserTrades(user?.id);

  // Biometric authentication
  const {
    isAvailable: isBiometricAvailable,
    isNativePlatform,
    biometryTypeName,
    hasCredentials: hasBiometricCredentials,
    deleteCredentials: deleteBiometricCredentials,
  } = useBiometric();

  // Subscription feature gating
  const { hasFeature } = useSubscription();
  const canHaveUnlimitedTrades = hasFeature('unlimitedTrades');

  // Feedback form state
  const [feedbackCategory, setFeedbackCategory] = useState<string>("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const feedbackInFlight = useRef(false);

  // Phone verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Profile preview modal state
  const [showProfilePreview, setShowProfilePreview] = useState(false);

  // Feedback submission mutation
  //
  // The success toast fires only on a 2xx. The backend now answers 502 when the report is stored
  // but could not be delivered to support, and `api.post` throws on any non-2xx — so a failed
  // delivery lands in onError and the user is told to retry, instead of being thanked for a
  // report no human received.
  const feedbackMutation = useMutation({
    mutationFn: async (data: { category: string; message: string }) => {
      return api.post("/api/feedback", { ...data, platform });
    },
    onSuccess: () => {
      showSuccess("Feedback Submitted", "Thank you for your feedback!");
      setFeedbackCategory("");
      setFeedbackMessage("");
    },
    onError: (error: any) => {
      showError(
        "Submission Failed",
        error.message || "Failed to submit feedback. Please try again."
      );
    },
    onSettled: () => {
      feedbackInFlight.current = false;
    },
  });

  const handleSubmitFeedback = () => {
    // A double tap on a touch screen fires two clicks before React re-renders the button as
    // disabled, and `feedbackMutation.isPending` read here is the value from the render that
    // produced this handler — still false on the second tap. A ref is the only guard that is
    // already true by then, so it is what stops the duplicate report.
    if (feedbackInFlight.current || feedbackMutation.isPending) {
      return;
    }
    if (!feedbackCategory) {
      showError("Category Required", "Please select a feedback category.");
      return;
    }
    if (!feedbackMessage.trim()) {
      showError("Message Required", "Please enter your feedback message.");
      return;
    }
    feedbackInFlight.current = true;
    feedbackMutation.mutate({
      category: feedbackCategory,
      message: feedbackMessage.trim(),
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      showSuccess("Logged out successfully");
      navigate("/login");
    } catch (error) {
      showError("Logout failed", "Please try again");
    }
  };

  // Handle sending verification code
  const handleSendVerificationCode = async () => {
    if (!user?.phone) return;
    try {
      await api.post('/api/auth/phone/send-code', { phoneNumber: user.phone });
      showSuccess('Verification code sent!', 'Check your phone for the SMS code');
    } catch (error: any) {
      showError('Failed to send code', error.message || 'Please try again later');
      throw error;
    }
  };

  // Handle verifying phone with OTP code
  const handleVerifyPhone = async (code: string) => {
    if (!user?.phone) return;
    try {
      await api.post('/api/auth/phone/verify-code', { phoneNumber: user.phone, code });
      // Refresh user data to get updated phone_confirmed_at
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      showSuccess('Phone verified!', 'Your phone number has been verified');
    } catch (error: any) {
      showError('Verification failed', error.message || 'Invalid code');
      throw error;
    }
  };

  // Handle company logo change
  // Loading state
  if (isLoading) {
    return (
      <AppLayout activeTab="profile" title="Settings">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading settings...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // User initials for avatar fallback
  const userInitials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`
    : user?.email?.[0]?.toUpperCase() || "U";

  const userDisplayName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.firstName || user?.lastName || "User";

  return (
    <AppLayout activeTab="profile" title="Settings">
      <div className="max-w-md md:max-w-2xl lg:max-w-4xl mx-auto px-4 pb-10 md:pb-0 space-y-6">
        {/* Profile Card - Hidden for org members */}
        {!isOrgMember && (
        <Card>
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <span className="flex-1">Professional Profile</span>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/edit-profile")}
                  className="bg-white/80 text-sm"
                >
                  Edit Profile
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowProfilePreview(true)}
                  className="bg-white/80 border-blue-300/60 hover:bg-blue-50/80 text-blue-700 text-sm"
                >
                  Preview
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Avatar and Company Logo Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column: Avatar */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-1">
                    Profile Image
                  </p>
                  <div className="flex items-center gap-4">
                    <Avatar className="w-20 h-20 ring-4 ring-white shadow-lg">
                      <AvatarImage src={user?.profileImageUrl} alt={userDisplayName} />
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xl font-bold">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </div>
              </div>

              {/* Right Column: Company Logo & Name */}
              <div className="space-y-4">
                {/* Company Logo */}
                <ProfessionalProfileGate
                  fallback={
                    <div className="space-y-2">
                      <p className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-1">
                        Company Logo
                      </p>
                      <div
                        onClick={() => navigate('/subscription')}
                        className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors"
                      >
                        <Building2 className="w-10 h-10 text-gray-300" />
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-blue-600 mb-0.5">
                            Upgrade to Pro
                          </p>
                          <p className="text-xs text-gray-600">
                            Company branding available on Pro and Grow plans
                          </p>
                        </div>
                      </div>
                    </div>
                  }
                >
                  <div className="space-y-2">
                    <p className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-1">
                      Company Logo
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden ring-4 ring-gray-100">
                        {user?.companyLogoUrl ? (
                          <img
                            src={user.companyLogoUrl}
                            alt="Company logo"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Building2 className="w-8 h-8 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                </ProfessionalProfileGate>
              </div>
            </div>

            {/* Primary Trade & Company Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Primary Trade Display (Read-only) */}
              <div>
                <p className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-1">
                  Primary Trade
                </p>
                <p className="text-lg text-gray-700 font-medium">
                  {user?.trigPrimaryTradeName || "Not set"}
                </p>
              </div>

              {/* Company Name */}
              <div>
                <p className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-1">
                  Company
                </p>
                {hasFeature('professionalProfile') ? (
                  <p className="text-lg text-gray-700 font-medium">
                    {user?.company || "No company listed"}
                  </p>
                ) : (
                  <div
                    onClick={() => navigate('/subscription')}
                    className="flex items-center gap-2 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors"
                  >
                    <ArrowUpCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-blue-600">
                        Upgrade to Pro
                      </span>
                      {' '}to list your company on your profile
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Phone className="h-4 w-4 text-gray-500" />
                  <p className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                    Phone
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-gray-700 font-medium">
                    {formatPhoneNumber(user?.phone)}
                  </p>

                  {/* Phone Verification Status - HIDDEN: Not supporting phone verification for now */}
                  {false && user?.phone && (
                    <div className="flex items-center gap-2">
                      {user?.phoneConfirmedAt ? (
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-green-100 rounded-md">
                          <Check className="w-3 h-3 text-green-600" />
                          <span className="text-xs text-green-700 font-medium">
                            Verified
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-amber-100 rounded-md">
                            <AlertCircle className="w-3 h-3 text-amber-600" />
                            <span className="text-xs text-amber-700 font-medium">
                              Not Verified
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowVerificationModal(true)}
                            className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                          >
                            Verify
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <p className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                    Email
                  </p>
                </div>
                <p className="text-sm text-gray-700 font-medium">
                  {user?.email}
                </p>
              </div>
            </div>

            {/* Additional Trades & Market Types - Two Column Layout */}
            {((userTrades && userTrades.length > 0) || (user?.marketTypes && user.marketTypes.length > 0)) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                {/* Additional Trades */}
                {userTrades && userTrades.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3">
                      Additional Trades
                    </p>
                    <div className="flex flex-wrap gap-2 items-center">
                      {/* Show all trades if user has unlimited, otherwise just the first one */}
                      {(canHaveUnlimitedTrades ? userTrades : [userTrades[0]]).map((trade) => (
                        <span
                          key={trade.id}
                          className="px-3 py-1.5 bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 text-xs font-medium rounded-full border border-blue-300/50"
                        >
                          {trade.name}
                        </span>
                      ))}
                      {/* Show upgrade prompt if user has multiple trades but is on a limited plan */}
                      {!canHaveUnlimitedTrades && userTrades.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate("/plans")}
                          className="h-auto px-3 py-1.5 text-xs font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 border border-orange-200 rounded-full"
                        >
                          <ArrowUpCircle className="w-3.5 h-3.5 mr-1" />
                          Upgrade for more trades
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Market Types */}
                {user?.marketTypes && user.marketTypes.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3">
                      Market Types
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {user.marketTypes.map((market: string, index: number) => (
                        <span
                          key={index}
                          className="px-3 py-1.5 bg-gradient-to-r from-green-100 to-green-200 text-green-800 text-xs font-medium rounded-full border border-green-300/50"
                        >
                          {market}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Service Areas */}
            {user?.serviceAreas && user.serviceAreas.length > 0 && (
              <div>
                <p className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3">
                  Service Areas
                </p>
                <p className="text-sm text-gray-700 font-medium leading-relaxed">
                  {user.serviceAreas.join(", ")}
                </p>
              </div>
            )}

          </CardContent>
        </Card>
        )}

        {/* Connected Accounts Card — the destination the login page's Apple
            "Hide My Email" notices point at (APPLE_HIDDEN_EMAIL_LINKING.md §6). */}
        <ConnectedAccountsCard />

        {/* Account & Support Card */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-gray-50 to-slate-50 border-b">
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center">
                <Settings className="h-5 w-5 text-gray-600" />
              </div>
              <span>Account & Support</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-2">
            {/* Account */}
            <Button
              variant="ghost"
              className="w-full justify-start h-auto p-4 rounded-xl hover:bg-blue-50/50 group"
              onClick={() => navigate("/account")}
            >
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-blue-200">
                <Settings className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900">
                  Account Preferences
                </div>
                <div className="text-sm text-gray-600">
                  Manage your account
                </div>
              </div>
            </Button>

            {/* Biometric Settings - Only show on native platforms */}
            {isNativePlatform && isBiometricAvailable && (
              <div className="w-full p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/50">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Fingerprint className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">
                      {biometryTypeName}
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      {hasBiometricCredentials
                        ? `Sign in quickly with ${biometryTypeName}`
                        : `Enable ${biometryTypeName} for faster sign-in`}
                    </div>
                    {hasBiometricCredentials ? (
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-100 rounded-md">
                          <Check className="w-3 h-3 text-emerald-600" />
                          <span className="text-xs text-emerald-700 font-medium">
                            Enabled
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const success = await deleteBiometricCredentials();
                            if (success) {
                              showSuccess(`${biometryTypeName} disabled`, "You can re-enable it on your next login");
                            } else {
                              showError("Failed to disable", "Please try again");
                            }
                          }}
                          className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
                        >
                          Disable
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">
                        You'll be prompted to enable {biometryTypeName} on your next login
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* FAQ & Help */}
            <Button
              variant="ghost"
              className="w-full justify-start h-auto p-4 rounded-xl hover:bg-green-50/50 group"
              onClick={() => navigate("/faq")}
            >
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-green-200">
                <HelpCircle className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900">FAQ & Help</div>
                <div className="text-sm text-gray-600">
                  Find answers to common questions
                </div>
              </div>
            </Button>

            {/* Contact Support */}
            <Button
              variant="ghost"
              className="w-full justify-start h-auto p-4 rounded-xl hover:bg-purple-50/50 group"
              onClick={() => navigate("/contact-us")}
            >
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-purple-200">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900">
                  Contact Support
                </div>
                <div className="text-sm text-gray-600">
                  Get help from our team
                </div>
              </div>
            </Button>

            {/* Plans - Hidden for org members */}
            {!isOrgMember && (
            <Button
              variant="ghost"
              className="w-full justify-start h-auto p-4 rounded-xl hover:bg-orange-50/50 group"
              onClick={() => navigate("/plans")}
            >
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-orange-200">
                <CreditCard className="w-5 h-5 text-orange-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900">
                  View / Change Plan
                </div>
                <div className="text-sm text-gray-600">
                  Manage your subscription
                </div>
              </div>
            </Button>
            )}

            {/* Organization Management - Grow Plan Feature - Hidden for org members */}
            {!isOrgMember && (
              <TeamCollaborationGate
                fallback={
                  <Button
                    variant="ghost"
                    className="w-full justify-start h-auto p-4 rounded-xl hover:bg-teal-50/50 group"
                    onClick={() => navigate("/plans")}
                  >
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-teal-200">
                      <Building2 className="w-5 h-5 text-teal-600" />
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-semibold text-gray-900 flex items-center gap-2">
                        Organization Management
                        <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-full">
                          Grow
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Upgrade to manage your team
                      </div>
                    </div>
                  </Button>
                }
              >
                <Button
                  variant="ghost"
                  className="w-full justify-start h-auto p-4 rounded-xl hover:bg-teal-50/50 group"
                  onClick={() => navigate("/organization")}
                >
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-teal-200">
                    <Building2 className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">
                      Organization Management
                    </div>
                    <div className="text-sm text-gray-600">
                      Manage your team and members
                    </div>
                  </div>
                </Button>
              </TeamCollaborationGate>
            )}

            {/* Legal */}
            <Button
              variant="ghost"
              className="w-full justify-start h-auto p-4 rounded-xl hover:bg-indigo-50/50 group"
              onClick={() => navigate("/legal")}
            >
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-indigo-200">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900">Legal</div>
                <div className="text-sm text-gray-600">
                  Privacy Policy & Terms of Service
                </div>
              </div>
            </Button>

            {/* Blocked Users */}
            <Button
              variant="ghost"
              className="w-full justify-start h-auto p-4 rounded-xl hover:bg-orange-50/50 group"
              onClick={() => navigate("/blocked-users")}
            >
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-orange-200">
                <Ban className="w-5 h-5 text-orange-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900">Blocked Users</div>
                <div className="text-sm text-gray-600">
                  Manage blocked users
                </div>
              </div>
            </Button>

            {/* Delete Account */}
            <Button
              variant="ghost"
              className="w-full justify-start h-auto p-4 rounded-xl hover:bg-red-50/50 group"
              onClick={() => navigate("/delete-account")}
            >
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-red-200">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-red-700">Delete / Deactivate Account</div>
                <div className="text-sm text-red-500">
                  Manage account deletion
                </div>
              </div>
            </Button>

            {/* Sign Out */}
            <div className="border-t border-gray-100 mt-4 pt-4">
              <Button
                variant="ghost"
                className="w-full justify-start h-auto p-4 rounded-xl hover:bg-gray-50/50 group"
                onClick={handleLogout}
              >
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-gray-200">
                  <LogOut className="w-5 h-5 text-gray-600" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900">Sign Out</div>
                  <div className="text-sm text-gray-600">
                    Sign out of your account
                  </div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Get the Mobile App Card.
            Settings is where someone already signed in on a laptop goes looking for "is there
            an app?", which is why the badges live here as well as on the marketing footer and
            the login page. The whole card is gated, not just the badge row: inside the native
            apps this would be a card advertising the app the user is reading it in. */}
        {APP_STORE_BADGES_VISIBLE && (
          <Card>
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Smartphone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <span>Get the Mobile App</span>
                  <p className="text-sm font-normal text-gray-600 mt-0.5">
                    Take Trade Loop with you on iPhone and Android
                  </p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <AppStoreBadges className="-ml-1" />
            </CardContent>
          </Card>
        )}

        {/* Feedback Submission Card */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-white" />
              </div>
              <div>
                <span>Feedback Submission</span>
                <p className="text-sm font-normal text-gray-600 mt-0.5">
                  Help us improve Trade Loop
                </p>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* Category Select */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Feedback Category <span className="text-red-500">*</span>
              </label>
              <Select
                value={feedbackCategory}
                onValueChange={setFeedbackCategory}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">
                    <div className="flex items-center gap-2">
                      <Bug className="w-4 h-4 text-red-500" />
                      <span>Bug Report</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="feature">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-yellow-500" />
                      <span>Feature Request</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="general">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-blue-500" />
                      <span>General Feedback</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Message Textarea */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Your Feedback <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                placeholder="Tell us what's on your mind... Describe a bug, suggest a feature, or share your thoughts."
                className="min-h-[120px] resize-none"
              />
              <p className="text-xs text-gray-500">
                {feedbackMessage.length}/1000 characters
              </p>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmitFeedback}
              disabled={
                feedbackMutation.isPending ||
                !feedbackCategory ||
                !feedbackMessage.trim()
              }
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
            >
              {feedbackMutation.isPending ? (
                "Submitting..."
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Feedback
                </>
              )}
            </Button>

            {/* Info Note */}
            <p className="text-xs text-gray-500 text-center pt-2">
              We review all feedback and use it to improve Trade Loop for
              everyone.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        phoneNumber={user?.phone || ''}
        onVerify={handleVerifyPhone}
        onResendCode={handleSendVerificationCode}
      />

      {/* Profile Preview Modal */}
      <ProfilePreviewModal
        isOpen={showProfilePreview}
        onClose={() => setShowProfilePreview(false)}
      />

      {/* Card 13 — the installed native build and the running web bundle. Settings is where
          people look for "what version do I have?", so it is shown here as well as on Account. */}
      <AppVersionFooter />
    </AppLayout>
  );
}
